import type { SolvePathOutput } from '@jeraisonne/ai';
import { supabase } from '@/lib/supabase';
import type { SubjectSlug, SchoolLevel } from '@jeraisonne/db';

export type GenerateLogEntry = {
  ts: string;
  level: 'info' | 'warn' | 'error';
  message: string;
};

export type GenerateLog = {
  started_at: string;
  finished_at: string;
  status: 'success' | 'error';
  model?: string;
  prompt_version?: string;
  input_tokens?: number;
  output_tokens?: number;
  entries: GenerateLogEntry[];
};

export type GenerateExerciseParams = {
  chapter_id: string;
  raw_statement: string;
  subject: SubjectSlug;
  level: SchoolLevel;
  custom_instructions?: string;
  partial_solve_path?: SolvePathOutput;
  api_key?: string;
  step_by_step?: boolean;
};

export type GenerateErrorCode =
  | 'API_ERROR'
  | 'EMPTY_RESPONSE'
  | 'INVALID_JSON'
  | 'SCHEMA_VIOLATION'
  | 'UNAUTHORIZED'
  | 'FORBIDDEN'
  | 'MISSING_STATEMENT'
  | 'NETWORK_ERROR';

export class GenerateError extends Error {
  constructor(
    public readonly code: GenerateErrorCode,
    message: string,
    public readonly rawOutput?: string,
  ) {
    super(message);
    this.name = 'GenerateError';
  }
}

export type GenerateResult = {
  output: SolvePathOutput;
  meta: {
    model: string;
    promptVersion: string;
    inputTokens: number;
    outputTokens: number;
  };
  log: GenerateLog;
};

export async function generateExercise(params: GenerateExerciseParams): Promise<GenerateResult> {
  const { data, error } = await supabase.functions.invoke('generate-exercise', {
    body: params,
  });

  if (error) {
    console.error('[generate-exercise] network/invoke error:', error.message, error);
    throw new GenerateError('NETWORK_ERROR', error.message);
  }

  if (data?.error) {
    console.error(
      '[generate-exercise] server error:',
      data.error,
      data.message,
      data.rawOutput?.slice(0, 500),
    );
    throw new GenerateError(
      data.error as GenerateErrorCode,
      data.message ?? 'Erreur de génération',
      data.rawOutput,
    );
  }

  if (!data?.output) {
    console.error('[generate-exercise] empty output in response:', data);
    throw new GenerateError('EMPTY_RESPONSE', 'Réponse vide du serveur');
  }

  return data as GenerateResult;
}

export function generateErrorMessage(code: GenerateErrorCode): string {
  switch (code) {
    case 'API_ERROR':
      return "Erreur de communication avec l'IA. Vérifie ta clé API ou réessaie.";
    case 'EMPTY_RESPONSE':
      return "L'IA n'a pas renvoyé de réponse. Réessaie.";
    case 'INVALID_JSON':
      return "L'IA a généré un format invalide. Réessaie ou reformule l'énoncé.";
    case 'SCHEMA_VIOLATION':
      return "L'IA a généré un exercice non conforme. Réessaie avec un énoncé plus précis.";
    case 'MISSING_STATEMENT':
      return "L'énoncé est requis.";
    case 'NETWORK_ERROR':
      return 'Erreur réseau. Vérifie ta connexion et réessaie.';
    case 'UNAUTHORIZED':
    case 'FORBIDDEN':
      return "Tu n'as pas les droits pour générer des exercices.";
    default:
      return 'Erreur inattendue. Réessaie.';
  }
}
